/**
 * Full-screen video turns on its side (Gordon 2026-11-01: "clicking on
 * full-screen video should turn the video on its side directly and full
 * screen").
 *
 * The installed app is locked to portrait by the manifest, and Android keeps
 * that lock in full screen, so a full-screen video stayed upright. While a
 * video, a video embed (YouTube's own full-screen button in News, Videos …) or
 * a wrapper marked `data-bb-video-fs` is full screen, the screen is locked to
 * the video's orientation — landscape, unless the element says
 * `data-bb-aspect` < 1 (a vertical video). Leaving full screen hands the
 * orientation back to the manifest. iPhones have neither element full screen
 * nor an orientation lock; callers rotate the frame themselves there
 * (`canElementFullscreen` false).
 */

type Doc = Document & { webkitFullscreenElement?: Element | null; webkitFullscreenEnabled?: boolean; webkitExitFullscreen?: () => Promise<void> | void };
type El = Element & { webkitRequestFullscreen?: () => Promise<void> | void };
type Orientation = { lock?: (o: string) => Promise<void>; unlock?: () => void };

export function fullscreenElement(): Element | null {
  if (typeof document === 'undefined') return null;
  const d = document as Doc;
  return d.fullscreenElement || d.webkitFullscreenElement || null;
}

function isVideoLike(el: Element): boolean {
  return el.matches('video, iframe, [data-bb-video-fs]');
}

/** Landscape unless the element is marked as a vertical video. */
export function videoOrientation(aspect: number | null | undefined): 'landscape' | 'portrait' {
  return typeof aspect === 'number' && aspect > 0 && aspect < 1 ? 'portrait' : 'landscape';
}

let lockedByUs = false;

/** Mounted once (app shell): every full-screen video locks to its orientation. */
export function installFullscreenRotation(): () => void {
  if (typeof document === 'undefined') return () => {};
  const orientation = (screen as unknown as { orientation?: Orientation }).orientation;
  const onChange = () => {
    if (!orientation?.lock) return;
    const el = fullscreenElement();
    if (el && isVideoLike(el)) {
      const aspect = parseFloat((el as HTMLElement).dataset?.bbAspect || '');
      orientation.lock(videoOrientation(Number.isFinite(aspect) ? aspect : null))
        .then(() => { lockedByUs = true; })
        .catch(() => { /* desktop / not allowed: nothing to turn */ });
    } else if (!el && lockedByUs) {
      lockedByUs = false;
      try { orientation.unlock?.(); } catch { /* ignore */ }
    }
  };
  document.addEventListener('fullscreenchange', onChange);
  document.addEventListener('webkitfullscreenchange', onChange);
  return () => {
    document.removeEventListener('fullscreenchange', onChange);
    document.removeEventListener('webkitfullscreenchange', onChange);
  };
}

/** False on iPhone (no element full screen) — the caller fakes it there. */
export function canElementFullscreen(el: Element | null): boolean {
  if (!el || typeof document === 'undefined') return false;
  const d = document as Doc;
  const e = el as El;
  return !!(d.fullscreenEnabled || d.webkitFullscreenEnabled) && !!(e.requestFullscreen || e.webkitRequestFullscreen);
}

export async function enterElementFullscreen(el: Element): Promise<void> {
  const e = el as El;
  await (e.requestFullscreen || e.webkitRequestFullscreen)?.call(e);
}

export async function exitElementFullscreen(): Promise<void> {
  const d = document as Doc;
  if (!fullscreenElement()) return;
  await (d.exitFullscreen || d.webkitExitFullscreen)?.call(d);
}
